/**
 * Hugin T300 Fiscal Device Integration
 * Hugin T300 Yazarkasa Entegrasyonu
 */

import {
  IFiscalDevice,
  FiscalProductItem,
  FiscalOperationResult,
  FiscalApiResponse,
} from './types';
import { fetchLocalServiceWithFallback } from './localServiceFetch';

// Yazarkasa API endpoint'leri (varsayılan, setLocalServiceUrl ile değiştirilebilir)
const DEFAULT_LOCAL_SERVICE_URL = 'http://localhost:3003';
let currentLocalServiceUrl = DEFAULT_LOCAL_SERVICE_URL;

// ✅ YENİ: Yerel servis URL'sini ayarla (QuickSaleScreen'den çağrılır)
export const setLocalServiceUrl = (url: string) => {
  if (url && url !== currentLocalServiceUrl) {
    console.log('[Hugin] 🔌 Yerel servis URL güncellendi:', url);
    currentLocalServiceUrl = url;
  }
};

// Dinamik API base URL'i hesapla
const getLocalServiceApiBaseUrl = () => `${currentLocalServiceUrl}/api/yazarkasa`;

// ✅ Yedek adrese (localhost:3003) yalnızca güvenli okuma çağrıları tekrar gönderilir;
// ödeme/satış komutları TEKRAR GÖNDERİLMEZ (çift çekim önlemi). Bkz. localServiceFetch.ts
const fetchWithFallback = (url: string, options: RequestInit): Promise<Response> =>
  fetchLocalServiceWithFallback(url, options, currentLocalServiceUrl, DEFAULT_LOCAL_SERVICE_URL);

// Yazarkasa API çağrıları için yardımcı fonksiyon
const callFiscalPrinterAPI = async (
  endpoint: string,
  body: any,
  showAlertModal?: (title: string, message: string) => void
): Promise<FiscalApiResponse> => {
  try {
    const response = await fetchWithFallback(`${getLocalServiceApiBaseUrl()}/${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: `HTTP error! status: ${response.status}`,
      }));
      console.error(`Fiscal Printer API error (${endpoint}):`, errorData);
      return { success: false, data: errorData, rawResponse: response };
    }
    const data = await response.json();
    if (
      data.success === false ||
      (data.ErrorCode !== undefined && data.ErrorCode !== 0 && data.ErrorCode !== null)
    ) {
      console.error(`Fiscal Printer operation error (${endpoint}):`, data);

      // Daha açıklayıcı hata mesajı
      let detailedError = data.details || data.ErrorMessage || data.message || 'Bilinmeyen hata';

      // Socket exception kontrolü
      if (detailedError && typeof detailedError === 'string' && detailedError.includes('SocketException')) {
        detailedError =
          '⚠️ Yazarkasa bağlantısı kurulamadı!\n\n' +
          'Lütfen kontrol edin:\n' +
          '1. Yazarkasa cihazı açık mı?\n' +
          '2. USB/Seri port bağlantısı yerinde mi?\n' +
          '3. Local Service (T300Bridge) çalışıyor mu?\n' +
          '4. Başka program yazarkasayı kullanıyor mu?\n\n' +
          'Teknik Detay: ' +
          detailedError.substring(0, 200);
      }

      return { success: false, data, rawResponse: response };
    }
    console.log(`Fiscal Printer API success (${endpoint}):`, data);
    return { success: true, data, rawResponse: response };
  } catch (error: any) {
    console.error(`Fiscal Printer API general error (${endpoint}):`, error);
    return { success: false, data: { message: error.message }, rawResponse: null };
  }
};

// Hugin T300 yazarkasa entegrasyonu
export class HuginT300Device implements IFiscalDevice {
  /**
   * Nakit ödeme için Hugin T300 yazarkasa işlemi
   */
  async processCashPayment(
    products: FiscalProductItem[],
    totalAmount: number,
    showAlertModal?: (title: string, message: string) => void
  ): Promise<FiscalOperationResult> {
    let documentId: string | null = null;

    try {
      console.log('[Hugin T300] Nakit ödeme işlemi başlıyor...');

      // ADIM 1: Belge Başlat
      const startDocResponse = await callFiscalPrinterAPI(
        'belge-baslat',
        { documentType: 'receipt' },
        showAlertModal
      );
      if (!startDocResponse || !startDocResponse.success) {
        throw new Error(startDocResponse?.data?.message || 'Yazarkasada belge başlatılamadı.');
      }
      documentId = startDocResponse.data?.data?.documentId || 'Bilinmiyor';
      console.log(`[Hugin T300] Belge başarıyla başlatıldı. ID: ${documentId}`);

      // ADIM 2: Satış JSON'ını Hazırla
      const fiscalItems = products.map((product, index) => ({
        Id: 0,
        Quantity: product.quantity,
        Price: parseFloat((product.price * (1 + product.vatRate / 100)).toFixed(2)),
        Name: product.name.substring(0, 22),
        DeptId: product.departmentNo,
        Status: 0,
      }));

      const huginSalesData = {
        FiscalItems: fiscalItems,
        Payments: [{ Type: 0, Index: 0, PaidTotal: totalAmount }],
        EndOfReceiptInfo: {
          CloseReceiptFlag: true,
          BarcodeFlag: false,
        },
      };

      console.log('[Hugin T300] Oluşturulan Satış JSON:', JSON.stringify(huginSalesData, null, 2));

      // ADIM 3: Departman Bazlı Satış JSON'ını Gönder
      const deptSaleResponse = await callFiscalPrinterAPI(
        'departman-toplu-satis',
        { salesJson: JSON.stringify(huginSalesData) },
        showAlertModal
      );
      if (!deptSaleResponse || !deptSaleResponse.success) {
        throw new Error(
          deptSaleResponse?.data?.message || 'Yazarkasa departman bazlı toplu satış işlemi başarısız oldu.'
        );
      }
      console.log('[Hugin T300] Departman bazlı toplu satış başarıyla tamamlandı:', deptSaleResponse.data);

      return {
        success: true,
        documentId: documentId || undefined,
      };
    } catch (error: any) {
      console.error('[Hugin T300] Nakit ödeme hatası:', error);

      // Hata durumunda belgeyi iptal et
      if (documentId && documentId !== 'Bilinmiyor') {
        console.log(`[Hugin T300] Hata nedeniyle belge (${documentId}) iptal ediliyor...`);
        try {
          const voidResponse = await fetchWithFallback(`${getLocalServiceApiBaseUrl()}/belge-iptal`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({}),
          });
          const voidResult = await voidResponse.json();
          if (voidResult.success) {
            console.log(`[Hugin T300] Belge (${documentId}) başarıyla iptal edildi.`);
          }
        } catch (voidError: any) {
          console.error(`[Hugin T300] Belge iptal edilirken hata:`, voidError);
        }
      }

      return {
        success: false,
        errorMessage: error.message || 'Bilinmeyen hata',
      };
    }
  }

  /**
   * POS ödeme için Hugin T300 yazarkasa işlemi
   * @param cashAmount Opsiyonel: Karma ödeme için nakit tutarı (parçalı ödemede kullanılır)
   */
  async processPosPayment(
    products: FiscalProductItem[],
    totalAmount: number,
    showAlertModal?: (title: string, message: string) => void,
    cashAmount?: number, // ✅ YENİ: Karma ödeme için nakit tutarı
    companyId?: string // ✅ PavoPay için satış kaydı (hugin'de kullanılmaz)
  ): Promise<FiscalOperationResult> {
    let documentId: string | null = null;

    try {
      console.log('[Hugin T300] POS ödeme işlemi başlıyor...');

      // 0. ÖNCELİKLE: Eğer açık bir belge varsa iptal et
      console.log('[Hugin T300] 0. Açık belge kontrolü yapılıyor...');
      try {
        await callFiscalPrinterAPI('belge-iptal', {});
        console.log('[Hugin T300] ⚠️ Önceki açık belge iptal edildi.');
      } catch {
        // Açık belge yoksa hata verir, göz ardı et
        console.log('[Hugin T300] ℹ️ Açık belge yok (normal).');
      }

      // 1. Belge Başlat
      console.log('[Hugin T300] 1. Belge başlatılıyor...');
      const belgeBaslatResponse = await callFiscalPrinterAPI('belge-baslat', {
        documentType: 'receipt',
      });

      if (!belgeBaslatResponse.success) {
        throw new Error(`Belge başlatılamadı: ${belgeBaslatResponse.data?.message || 'Bilinmeyen hata'}`);
      }

      documentId =
        belgeBaslatResponse.data?.documentId || belgeBaslatResponse.data?.id || 'Bilinmiyor';
      console.log('[Hugin T300] ✅ Belge başarıyla başlatıldı. ID:', documentId);

      // 2. Ürünleri TEK TEK Ekle
      console.log(`[Hugin T300] 2. ${products.length} ürün tek tek ekleniyor...`);

      for (let i = 0; i < products.length; i++) {
        const product = products[i];
        const productName = product.name.length > 22 ? product.name.substring(0, 22) : product.name;

        // ✅ product.price KDV HARİÇ geliyor, ama EFT toplamı KDV DAHİL olmalı!
        // ÇÖZÜM: KDV'yi ekleyelim ve vatRate=0 gönderelim ki yazarkasa tekrar KDV eklemesin
        const priceWithVat = product.price * (1 + product.vatRate / 100);
        
        console.log(`   [${i + 1}/${products.length}] Ekleniyor: ${productName} x ${product.quantity}`);
        console.log(`   Fiyat (KDV Dahil): ${priceWithVat.toFixed(2)} TL`);

        const addItemResponse = await callFiscalPrinterAPI('urun-ekle', {
          pluNo: product.departmentNo.toString(),
          barcode: product.barcode || '',
          name: productName,
          quantity: product.quantity,
          price: priceWithVat, // ✅ KDV DAHİL fiyat gönder
          vatRate: 0, // ✅ vatRate=0 (zaten KDV dahil fiyat gönderiyoruz)
          departmentId: product.departmentNo,
          isWeighable: false,
        });

        if (!addItemResponse.success) {
          throw new Error(`Ürün ekleme başarısız: ${productName}`);
        }

        console.log(`   ✅ Ürün ${i + 1}/${products.length} eklendi: ${productName}`);
      }

      console.log('[Hugin T300] ✅ Tüm ürünler başarıyla eklendi');

      // 3. EFT Yetkilendirme İşlemi
      console.log('[Hugin T300] 3. EFT yetkilendirme işlemi başlatılıyor...');
      const eftPayload = {
        amount: totalAmount,
        installment: 0,
        cardNumber: '',
      };

      const eftResponse = await callFiscalPrinterAPI('eft-yetkilendirme', eftPayload);

      if (!eftResponse.success || (eftResponse.data && eftResponse.data.ErrorCode !== 0)) {
        // EFT başarısız, belgeyi iptal et
        console.error('[Hugin T300] ❌ EFT yetkilendirme başarısız:', eftResponse);

        await callFiscalPrinterAPI('belge-iptal', {});

        throw new Error(
          `EFT yetkilendirme başarısız: ${eftResponse.data?.ErrorMessage || eftResponse.data?.message || 'Bilinmeyen hata'}`
        );
      }

      console.log('[Hugin T300] ✅ EFT yetkilendirme başarılı:', eftResponse);

      // EFT bilgileri
      const eftData = eftResponse.data || {};
      const fiscalData = {
        AuthCode: eftData.AuthCode || null,
        BatchNo: eftData.BatchNo || null,
        MaskedCardNumber: eftData.MaskedCardNumber || null,
        FiscalTransactionId: eftData.RawResult || null,
        CardType: eftData.CardType || 'credit_card',
      };

      // ✅ YENİ: Eğer cashAmount varsa (karma ödeme), nakit ödeme de ekle
      if (cashAmount && cashAmount > 0) {
        console.log(`[Hugin T300] 3.5. Nakit ödeme ekleniyor: ${cashAmount} TL`);
        const cashPaymentResponse = await callFiscalPrinterAPI('odeme-ekle', {
          paymentTypeIndex: 0, // 0 = NAKİT
          amount: cashAmount,
        });

        if (!cashPaymentResponse.success) {
          console.warn('[Hugin T300] ⚠️ Nakit ödeme eklenemedi:', cashPaymentResponse);
          // Hata durumunda belgeyi iptal et
          await callFiscalPrinterAPI('belge-iptal', {});
          throw new Error(`Nakit ödeme eklenemedi: ${cashPaymentResponse.data?.message || 'Bilinmeyen hata'}`);
        }

        console.log('[Hugin T300] ✅ Nakit ödeme başarıyla eklendi');
      }

      // 4. Belge Kapat
      console.log('[Hugin T300] 4. Belge kapatılıyor...');
      const belgeKapatResponse = await callFiscalPrinterAPI('belge-kapat', {
        printCopy: false,
      });

      if (!belgeKapatResponse.success) {
        throw new Error(
          `Belge kapatılamadı: ${belgeKapatResponse.data?.ErrorMessage || belgeKapatResponse.data?.message || 'Bilinmeyen hata'}`
        );
      }

      console.log('[Hugin T300] ✅ Belge başarıyla kapatıldı:', belgeKapatResponse);

      return {
        success: true,
        documentId: documentId || undefined,
        fiscalData,
      };
    } catch (error: any) {
      console.error('[Hugin T300] POS ödeme hatası:', error);

      // Hata durumunda belgeyi iptal et
      if (documentId && documentId !== 'Bilinmiyor') {
        console.log(`[Hugin T300] Hata nedeniyle belge (${documentId}) iptal ediliyor...`);
        try {
          await callFiscalPrinterAPI('belge-iptal', {});
        } catch (voidError) {
          console.error('[Hugin T300] Belge iptal edilirken hata:', voidError);
        }
      }

      return {
        success: false,
        errorMessage: error.message || 'Bilinmeyen hata',
      };
    }
  }

  /**
   * Yazarkasa bağlantı kontrolü
   */
  async checkConnection(): Promise<boolean> {
    try {
      const response = await fetchWithFallback(`${getLocalServiceApiBaseUrl()}/status`, { method: 'GET' });
      return response.ok;
    } catch (error) {
      return false;
    }
  }

  /**
   * Yazarkasa bilgilerini al
   */
  async getDeviceInfo(): Promise<any> {
    try {
      const response = await fetchWithFallback(`${getLocalServiceApiBaseUrl()}/device-info`, { method: 'GET' });
      if (response.ok) {
        return await response.json();
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  /**
   * ✅ YENİ: Çoklu ödeme işlemi (Birden fazla POS + NAKİT)
   * Parçalı ödeme modalı için - Tüm ödemeleri tek fişte işler
   * Her adımda geri bildirim verir (stepCallback)
   */
  async processMultiplePayments(
    products: FiscalProductItem[],
    posPayments: { id: string; amount: number }[],
    cashAmount?: number,
    showAlertModal?: (title: string, message: string) => void,
    stepCallback?: any // PaymentStepCallback türü
  ): Promise<FiscalOperationResult> {
    let documentId: string | null = null;

    try {
      console.log('[Hugin T300] Çoklu ödeme işlemi başlıyor...');
      console.log(`  POS Ödemeleri: ${posPayments.length} adet`);
      console.log(`  NAKİT: ${cashAmount || 0} TL`);

      // 0. Açık belge kontrolü
      try {
        await callFiscalPrinterAPI('belge-iptal', {});
      } catch {
        // Ignore
      }

      // 1. Belge Başlat
      console.log('[Hugin T300] 1. Belge başlatılıyor...');
      const belgeBaslatResponse = await callFiscalPrinterAPI('belge-baslat', {
        documentType: 'receipt',
      });

      if (!belgeBaslatResponse.success) {
        throw new Error(`Belge başlatılamadı: ${belgeBaslatResponse.data?.message || 'Bilinmeyen hata'}`);
      }

      documentId = belgeBaslatResponse.data?.documentId || 'Bilinmiyor';
      console.log('[Hugin T300] ✅ Belge başlatıldı. ID:', documentId);

      // 2. Ürünleri Ekle
      console.log(`[Hugin T300] 2. ${products.length} ürün ekleniyor...`);
      for (let i = 0; i < products.length; i++) {
        const product = products[i];
        const productName = product.name.length > 22 ? product.name.substring(0, 22) : product.name;
        
        // ✅ KDV DAHİL fiyat gönder, vatRate=0
        const priceWithVat = product.price * (1 + product.vatRate / 100);
        console.log(`   [${i + 1}/${products.length}] ${productName} x ${product.quantity}: ${priceWithVat.toFixed(2)} TL (KDV Dahil)`);

        const addItemResponse = await callFiscalPrinterAPI('urun-ekle', {
          pluNo: product.departmentNo.toString(),
          barcode: product.barcode || '',
          name: productName,
          quantity: product.quantity,
          price: priceWithVat, // ✅ KDV DAHİL
          vatRate: 0, // ✅ vatRate=0
          departmentId: product.departmentNo,
          isWeighable: false,
        });

        if (!addItemResponse.success) {
          throw new Error(`Ürün ekleme başarısız: ${productName}`);
        }
      }

      console.log('[Hugin T300] ✅ Tüm ürünler eklendi');

      // 3. POS Ödemelerini Ekle (Her biri için EFT yetkilendirme)
      const allFiscalData: any[] = [];
      
      for (let i = 0; i < posPayments.length; i++) {
        const posPayment = posPayments[i];
        const stepIndex = i;
        
        // ✅ ADIM BAŞLADI callback
        stepCallback?.onStepStart?.(stepIndex, 'pos', posPayment.amount, `${i + 1}. POS Ödeme`);
        
        console.log(`[Hugin T300] 3.${i + 1}. POS ödemesi: ${posPayment.amount} TL`);

        try {
          const eftResponse = await callFiscalPrinterAPI('eft-yetkilendirme', {
            amount: posPayment.amount,
            installment: 0,
            cardNumber: '',
          });

          if (!eftResponse.success || (eftResponse.data && eftResponse.data.ErrorCode !== 0)) {
            const errorMsg = eftResponse.data?.ErrorMessage || 'Bilinmeyen hata';
            
            // ✅ ADIM HATALI callback
            stepCallback?.onStepError?.(stepIndex, 'pos', posPayment.amount, errorMsg);
            
            // Hata durumunda DURDUR ve kullanıcıya bildir (belge iptal edilmeyecek, retry için açık kalacak)
            throw new Error(`${i + 1}. POS ödemesi başarısız: ${errorMsg}`);
          }

          console.log(`[Hugin T300] ✅ ${i + 1}. POS ödemesi başarılı`);
          
          // Fiscal data'yı kaydet
          const eftData = eftResponse.data || {};
          const fiscalDataItem = {
            paymentId: posPayment.id,
            AuthCode: eftData.AuthCode || null,
            BatchNo: eftData.BatchNo || null,
            MaskedCardNumber: eftData.MaskedCardNumber || null,
            FiscalTransactionId: eftData.RawResult || null,
            CardType: eftData.CardType || 'credit_card',
          };
          allFiscalData.push(fiscalDataItem);
          
          // ✅ ADIM BAŞARILI callback
          stepCallback?.onStepSuccess?.(stepIndex, 'pos', posPayment.amount, fiscalDataItem);
        } catch (stepError: any) {
          // Bu POS ödemesi başarısız oldu - hatayı yukarıya fırlat
          throw stepError;
        }
      }

      // 4. NAKİT Ödeme Ekle (varsa)
      if (cashAmount && cashAmount > 0) {
        const cashStepIndex = posPayments.length; // POS'lardan sonraki index
        
        // ✅ ADIM BAŞLADI callback
        stepCallback?.onStepStart?.(cashStepIndex, 'cash', cashAmount, 'Nakit Ödeme');
        
        console.log(`[Hugin T300] 4. Nakit ödeme ekleniyor: ${cashAmount} TL`);
        
        try {
          const cashPaymentResponse = await callFiscalPrinterAPI('odeme-ekle', {
            paymentTypeIndex: 0, // NAKİT
            subPaymentIndex: 0,  // ✅ subPaymentIndex eklendi (NAKİT için 0)
            amount: cashAmount,
          });

          if (!cashPaymentResponse.success) {
            const errorMsg = cashPaymentResponse.data?.message || 'Bilinmeyen hata';
            
            // ✅ ADIM HATALI callback
            stepCallback?.onStepError?.(cashStepIndex, 'cash', cashAmount, errorMsg);
            
            throw new Error(`Nakit ödeme eklenemedi: ${errorMsg}`);
          }

          console.log('[Hugin T300] ✅ Nakit ödeme eklendi');
          
          // ✅ ADIM BAŞARILI callback
          stepCallback?.onStepSuccess?.(cashStepIndex, 'cash', cashAmount);
        } catch (stepError: any) {
          throw stepError;
        }
      }

      // 5. Belge Kapat
      console.log('[Hugin T300] 5. Belge kapatılıyor...');
      const belgeKapatResponse = await callFiscalPrinterAPI('belge-kapat', {
        printCopy: false,
      });

      if (!belgeKapatResponse.success) {
        throw new Error(`Belge kapatılamadı: ${belgeKapatResponse.data?.message || 'Bilinmeyen hata'}`);
      }

      console.log('[Hugin T300] ✅ Belge başarıyla kapatıldı');

      return {
        success: true,
        documentId: documentId || undefined,
        fiscalData: {
          posPayments: allFiscalData, // Tüm POS ödemelerinin bilgileri
        } as any,
      };
    } catch (error: any) {
      console.error('[Hugin T300] Çoklu ödeme hatası:', error);

      // ✅ ÖNEMLİ: Hata durumunda belgeyi HEMEN iptal etme
      // Kullanıcı "Yeniden Dene" seçeneğini kullanabilir veya "Tümünü İptal Et"i seçebilir
      // Bu yüzden belge açık kalacak

      return {
        success: false,
        errorMessage: error.message || 'Bilinmeyen hata',
        documentId: documentId || undefined, // ✅ Belge ID'yi döndür (iptal için gerekebilir)
      };
    }
  }

  /**
   * ✅ YENİ: "Canlı Belge" Modu - Belge başlat ve ürünleri ekle
   * Bu fonksiyon belgeyi başlatır ve tüm ürünleri ekler, ama ödeme eklemez ve belgeyi kapatmaz
   */
  async startDocumentWithItems(
    products: FiscalProductItem[],
    showAlertModal?: (title: string, message: string) => void,
    totalAmount?: number
  ): Promise<FiscalOperationResult> {
    try {
      console.log('[Hugin T300] 🟢 CANLI BELGE MODU: Belge başlatılıyor ve ürünler ekleniyor...');

      // 0. Açık belge kontrolü (varsa iptal et)
      try {
        await callFiscalPrinterAPI('belge-iptal', {});
        console.log('[Hugin T300] ⚠️ Önceki açık belge iptal edildi.');
      } catch {
        console.log('[Hugin T300] ℹ️ Açık belge yok (normal).');
      }

      // 1. Belge Başlat
      console.log('[Hugin T300] 1. Belge başlatılıyor...');
      const belgeBaslatResponse = await callFiscalPrinterAPI('belge-baslat', {
        documentType: 'receipt',
      });

      if (!belgeBaslatResponse.success) {
        throw new Error(`Belge başlatılamadı: ${belgeBaslatResponse.data?.message || 'Bilinmeyen hata'}`);
      }

      const documentId = belgeBaslatResponse.data?.documentId || 'Bilinmiyor';
      console.log('[Hugin T300] ✅ Belge başlatıldı. ID:', documentId);

      // 2. Ürünleri Ekle
      console.log(`[Hugin T300] 2. ${products.length} ürün ekleniyor...`);
      for (let i = 0; i < products.length; i++) {
        const product = products[i];
        const productName = product.name.length > 22 ? product.name.substring(0, 22) : product.name;
        
        const priceWithVat = product.price * (1 + product.vatRate / 100);
        console.log(`   [${i + 1}/${products.length}] ${productName} x ${product.quantity}: ${priceWithVat.toFixed(2)} TL (KDV Dahil)`);

        const addItemResponse = await callFiscalPrinterAPI('urun-ekle', {
          pluNo: product.departmentNo.toString(),
          barcode: product.barcode || '',
          name: productName,
          quantity: product.quantity,
          price: priceWithVat,
          vatRate: 0,
          departmentId: product.departmentNo,
          isWeighable: false,
        });

        if (!addItemResponse.success) {
          // Hata durumunda belgeyi iptal et
          await callFiscalPrinterAPI('belge-iptal', {});
          throw new Error(`Ürün ekleme başarısız: ${productName}`);
        }
      }

      console.log('[Hugin T300] ✅ Tüm ürünler eklendi. Belge AÇIK - Ödemeler bekliyor...');

      return {
        success: true,
        documentId: documentId || undefined,
      };
    } catch (error: any) {
      console.error('[Hugin T300] Belge başlatma hatası:', error);
      
      // Hata durumunda belgeyi iptal etmeye çalış
      try {
        await callFiscalPrinterAPI('belge-iptal', {});
      } catch {
        // Ignore
      }

      return {
        success: false,
        errorMessage: error.message || 'Bilinmeyen hata',
      };
    }
  }

  /**
   * ✅ YENİ: "Canlı Belge" Modu - Tek bir ödeme ekle (POS veya NAKİT)
   */
  async addSinglePayment(
    paymentType: 'pos' | 'cash',
    amount: number,
    showAlertModal?: (title: string, message: string) => void
  ): Promise<FiscalOperationResult> {
    try {
      console.log(`[Hugin T300] 💳 Ödeme ekleniyor: ${paymentType.toUpperCase()} - ${amount} TL`);

      if (paymentType === 'pos') {
        // POS ödeme - EFT yetkilendirme
        const eftResponse = await callFiscalPrinterAPI('eft-yetkilendirme', {
          amount: amount,
          installment: 0,
          cardNumber: '',
        });

        if (!eftResponse.success || (eftResponse.data && eftResponse.data.ErrorCode !== 0)) {
          const errorMsg = eftResponse.data?.ErrorMessage || 'Bilinmeyen hata';
          throw new Error(`POS ödemesi başarısız: ${errorMsg}`);
        }

        console.log('[Hugin T300] ✅ POS ödemesi başarıyla eklendi');

        const eftData = eftResponse.data || {};
        return {
          success: true,
          fiscalData: {
            AuthCode: eftData.AuthCode || null,
            AcquirerId: eftData.AcquirerId || null, // ✅ YENİ: Acquirer ID
            BatchNo: eftData.BatchNo || null,
            StanNo: eftData.StanNo || null, // ✅ YENİ: Stan No
            MaskedCardNumber: eftData.MaskedCardNumber || null,
            BIN: eftData.BIN || null,
            PaidAmount: eftData.PaidAmount || amount.toString(), // ✅ YENİ: Ödenen tutar (iade için)
            FiscalTransactionId: eftData.RawResult || null,
            CardType: eftData.CardType || 'credit_card',
          },
        };
      } else {
        // NAKİT ödeme
        const cashPaymentResponse = await callFiscalPrinterAPI('odeme-ekle', {
          paymentTypeIndex: 0, // NAKİT
          subPaymentIndex: 0,
          amount: amount,
        });

        if (!cashPaymentResponse.success) {
          const errorMsg = cashPaymentResponse.data?.message || 'Bilinmeyen hata';
          throw new Error(`Nakit ödeme eklenemedi: ${errorMsg}`);
        }

        console.log('[Hugin T300] ✅ Nakit ödeme başarıyla eklendi');

        return {
          success: true,
        };
      }
    } catch (error: any) {
      console.error(`[Hugin T300] Ödeme ekleme hatası (${paymentType}):`, error);

      return {
        success: false,
        errorMessage: error.message || 'Bilinmeyen hata',
      };
    }
  }

  /**
   * ✅ YENİ: "Canlı Belge" Modu - Belgeyi kapat
   */
  async closeDocument(
    showAlertModal?: (title: string, message: string) => void
  ): Promise<FiscalOperationResult> {
    try {
      console.log('[Hugin T300] 🔒 Belge kapatılıyor...');

      const belgeKapatResponse = await callFiscalPrinterAPI('belge-kapat', {
        printCopy: false,
      });

      if (!belgeKapatResponse.success) {
        throw new Error(
          `Belge kapatılamadı: ${belgeKapatResponse.data?.message || 'Bilinmeyen hata'}`
        );
      }

      console.log('[Hugin T300] ✅ Belge başarıyla kapatıldı');

      return {
        success: true,
      };
    } catch (error: any) {
      console.error('[Hugin T300] Belge kapatma hatası:', error);

      return {
        success: false,
        errorMessage: error.message || 'Bilinmeyen hata',
      };
    }
  }

  /**
   * ✅ YENİ: "Canlı Belge" Modu - Açık belgeyi iptal et
   */
  async cancelDocument(
    showAlertModal?: (title: string, message: string) => void
  ): Promise<FiscalOperationResult> {
    try {
      console.log('[Hugin T300] ❌ Açık belge iptal ediliyor...');

      const response = await callFiscalPrinterAPI('belge-iptal', {});

      if (!response.success) {
        throw new Error(`Belge iptal edilemedi: ${response.data?.message || 'Bilinmeyen hata'}`);
      }

      console.log('[Hugin T300] ✅ Belge başarıyla iptal edildi');

      return {
        success: true,
      };
    } catch (error: any) {
      console.error('[Hugin T300] Belge iptal hatası:', error);

      return {
        success: false,
        errorMessage: error.message || 'Bilinmeyen hata',
      };
    }
  }

  /**
   * ✅ YENİ: POS ödemesini iade et (RefundEFTPayment veya VoidEFTPayment)
   */
  async refundPosPayment(
    paymentSequenceNo: number,
    showAlertModal?: (title: string, message: string) => void,
    fiscalData?: any
  ): Promise<FiscalOperationResult> {
    try {
      console.log(`[Hugin T300] 💳 POS ödemesi iade ediliyor... Sıra No: ${paymentSequenceNo}`);
      console.log(`[Hugin T300] EFT Bilgileri:`, fiscalData);

      // ⚠️ ÖNEMLI KISITLAMA: Hugin T300'de açık belge varken POS iadesi yapılamaz!
      // RefundEFTPayment sadece belge kapalıyken çalışır
      // VoidPayment ise sadece belgeden siler, POS'a iptal komutu göndermez
      
      const batchNo = fiscalData?.BatchNo ? parseInt(fiscalData.BatchNo) : 0;
      const stanNo = fiscalData?.StanNo ? parseInt(fiscalData.StanNo) : 0;
      const acquirerId = fiscalData?.AcquirerId ? parseInt(fiscalData.AcquirerId) : 0;

      if (!acquirerId) {
        throw new Error('AcquirerId bilgisi eksik. İade yapılamaz.');
      }

      let response;
      let isPartialRefund = false; // ✅ Kısmi iade flag'i
      
      if (batchNo === 0 || stanNo === 0) {
        // ⚠️ Batch henüz kapanmamış
        // SORUN: Açık belge varken RefundEFTPayment kullanılamıyor (ERR_INVALID_STATE)
        // GEÇİCİ ÇÖZÜM: VoidPayment kullan (sadece belgeden siler, gerçek iade DEĞİL!)
        console.warn(`[Hugin T300] ⚠️ DİKKAT: Açık belge varken POS iadesi yapılamaz!`);
        console.warn(`[Hugin T300] ⚠️ VoidPayment kullanılacak (sadece belgeden silinecek, karttan çekilen para iade EDİLMEYECEK!)`);
        
        // ⚠️ UYARI: showAlertModal burada ÇAĞRILMIYOR - PartialPaymentModal'da birleştirilerek gösterilecek
        
        // VoidPayment kullan (sadece belgeden siler)
        response = await callFiscalPrinterAPI('odeme-iptal', {
          eftInfo: null,
          paymentIndex: paymentSequenceNo,
        });
        
        // ✅ İade bilgisini flag'e kaydet
        if (response.success) {
          isPartialRefund = true; // Sadece belgeden silindi, gerçek iade DEĞİL
        }
      } else {
        // ✅ Batch kapanmış - VoidEFTPayment kullan
        console.log(`[Hugin T300] ✅ Batch kapanmış (BatchNo=${batchNo}, StanNo=${stanNo}). VoidEFTPayment kullanılıyor...`);
        
        response = await callFiscalPrinterAPI('odeme-iptal', {
          eftInfo: {
            acquirerId: acquirerId,
            batchNo: batchNo,
            stanNo: stanNo,
          },
          paymentIndex: null,
        });
      }

      if (!response.success) {
        throw new Error(`POS ödemesi iade edilemedi: ${response.data?.message || response.data?.ErrorMessage || 'Bilinmeyen hata'}`);
      }

      console.log('[Hugin T300] ✅ POS ödemesi başarıyla iade edildi');

      return {
        success: true,
        fiscalData: response.data,
        isPartialRefund: isPartialRefund, // ✅ Kısmi iade bilgisi
      };
    } catch (error: any) {
      console.error('[Hugin T300] POS iade hatası:', error);

      return {
        success: false,
        errorMessage: error.message || 'Bilinmeyen hata',
      };
    }
  }

  /**
   * ✅ YENİ: Belge kapandıktan sonra POS ödemesini iade et (RefundEFTPayment)
   */
  async refundPosPaymentAfterClose(
    amount: number,
    acquirerId: string | undefined,
    showAlertModal?: (title: string, message: string) => void
  ): Promise<FiscalOperationResult> {
    try {
      console.log(`[Hugin T300] 💳 Belge kapandıktan sonra POS ödemesi iade ediliyor... Tutar: ${amount} TL`);

      if (!acquirerId) {
        throw new Error('AcquirerId bilgisi eksik. İade yapılamaz.');
      }

      const acquirerIdNum = parseInt(acquirerId);

      // ✅ Belge kapandı, RefundEFTPayment kullanabiliriz!
      console.log(`[Hugin T300] ✅ RefundEFTPayment kullanılıyor (AcquirerId=${acquirerIdNum}, Amount=${amount})`);
      
      const response = await callFiscalPrinterAPI('eft-iade', {
        acquirerId: acquirerIdNum,
        amount: amount,
      });

      if (!response.success) {
        throw new Error(`POS ödemesi iade edilemedi: ${response.data?.message || response.data?.ErrorMessage || 'Bilinmeyen hata'}`);
      }

      console.log('[Hugin T300] ✅ POS ödemesi başarıyla iade edildi (belge kapandıktan sonra)');

      return {
        success: true,
        fiscalData: response.data,
      };
    } catch (error: any) {
      console.error('[Hugin T300] POS iade hatası (belge kapandıktan sonra):', error);

      return {
        success: false,
        errorMessage: error.message || 'Bilinmeyen hata',
      };
    }
  }

  /**
   * ✅ YENİ: Nakit ödemesini iade et (VoidPayment)
   */
  async refundCashPayment(
    paymentSequenceNo: number,
    showAlertModal?: (title: string, message: string) => void
  ): Promise<FiscalOperationResult> {
    try {
      console.log(`[Hugin T300] 💵 Nakit ödemesi iade ediliyor... Sıra No: ${paymentSequenceNo}`);

      const response = await callFiscalPrinterAPI('odeme-iptal', {
        eftInfo: null, // ✅ Açıkça null olarak belirt
        paymentIndex: paymentSequenceNo,
      });

      if (!response.success) {
        throw new Error(`Nakit ödemesi iade edilemedi: ${response.data?.message || 'Bilinmeyen hata'}`);
      }

      console.log('[Hugin T300] ✅ Nakit ödemesi başarıyla iade edildi');

      return {
        success: true,
        fiscalData: response.data,
      };
    } catch (error: any) {
      console.error('[Hugin T300] Nakit iade hatası:', error);

      return {
        success: false,
        errorMessage: error.message || 'Bilinmeyen hata',
      };
    }
  }
}

// Singleton instance
export const huginT300Device = new HuginT300Device();

